/**
 * Auth Provider - JWT Based (PostgreSQL)
 * Firebase yerine JWT kullanır
 */

import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import * as authService from './services/authService';
import { removeAuthToken, getAuthToken } from './services/apiClient';

interface User {
  id: string;
  email: string;
  display_name?: string;
  role: string;
  company_id?: string;
}

interface AuthContextType {
  user: User | null;
  selectedRole: string | null;
  setSelectedRole: (role: string | null) => void;
  initializing: boolean;
  signOut: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (data: any) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): React.ReactElement {
  const [user, setUser] = useState<User | null>(null);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [initializing, setInitializing] = useState<boolean>(true);

  // İlk yüklemede token kontrolü
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Check if this is an admin page - don't initialize normal user auth on admin pages
        const isAdminPage = window.location.pathname.startsWith('/admin');
        if (isAdminPage) {
          // Admin pages use separate authentication, skip normal user auth initialization
          setInitializing(false);
          return;
        }
        
        // Eğer role-signin sayfasındaysak, selectedRole'ü set etme (kullanıcı role seçsin)
        const isRoleSignInPage = window.location.pathname === '/role-signin';
        
        const token = getAuthToken();
        
        if (token) {
          // Token varsa user bilgisini localStorage'dan al
          const user = authService.getCurrentUser();
          
          if (user) {
            setUser(user);
            
            // Profil fotoğrafı için user data'yı backend'den refresh et
            // URL formatında olmayan (base64 veya eksik) photo_url varsa refresh et
            const currentPhotoUrl = (user as any).photo_url;
            // Eğer photo_url yoksa veya base64 formatındaysa (data: ile başlıyorsa) refresh et
            const shouldRefresh = !currentPhotoUrl || (currentPhotoUrl && currentPhotoUrl.startsWith('data:'));
            
            if (shouldRefresh) {
              try {
                const refreshedUser = await authService.refreshUser();
                if (refreshedUser) {
                  setUser(refreshedUser);
                }
              } catch (error) {
                // Hata durumunda localStorage'daki user data'yı kullan
                console.error('User refresh error:', error);
              }
            }
            
            // Role-signin sayfasındaysak, selectedRole'ü set etme
            if (isRoleSignInPage) {
              setSelectedRole(null);
              setInitializing(false);
              return;
            }
            
            // ÖNEMLİ: localStorage'daki selectedRole'ü kontrol et
            // Admin/Owner kullanıcıları farklı role'lere geçebilir (personnel, manager, vb.)
            // Bu yüzden savedRole varsa ve geçerliyse, onu koruyalım
            const savedRole = localStorage.getItem('selectedRole');
            const userRealRole = user.role;
            
            // Geçerli role listesi
            const validRoles = ['admin', 'owner', 'personnel', 'manager', 'staff'];
            
            if (savedRole && validRoles.includes(savedRole)) {
              // savedRole geçerli bir role ise, onu kullan
              // Admin/Owner kullanıcıları personel role'üne geçebilir, bu durumda savedRole 'personnel' olabilir
              setSelectedRole(savedRole);
            } else if (userRealRole === 'admin' || userRealRole === 'owner') {
              // Admin/Owner kullanıcısı için savedRole yoksa veya geçersizse, user'ın gerçek role'ünü kullan
              localStorage.setItem('selectedRole', userRealRole);
              setSelectedRole(userRealRole);
            } else {
              // Diğer roller için (personnel, staff, manager) savedRole yoksa veya geçersizse temizle
              // Kullanıcı role-signin sayfasına yönlendirilecek
              if (savedRole && !validRoles.includes(savedRole)) {
                localStorage.removeItem('selectedRole');
                localStorage.removeItem('selectedPersonnelId');
                localStorage.removeItem('selectedUserId');
              }
              setSelectedRole(null);
            }
            
            // Permission cache'lerini temizle (eski role'ün cache'i kalmasın)
            Object.keys(localStorage).forEach(key => {
              if (key.startsWith('cache_permissions_') || key.startsWith('cache_user_')) {
                localStorage.removeItem(key);
              }
            });
          } else {
            // Token var ama user yok, temizle
            removeAuthToken();
            setUser(null);
            setSelectedRole(null);
          }
        } else {
          // Token yok, user state'ini temizle
          setUser(null);
          setSelectedRole(null);
        }
      } catch (error) {
        removeAuthToken();
        setUser(null);
        setSelectedRole(null);
      } finally {
        setInitializing(false);
      }
    };

    initAuth();
    
    // User güncelleme event'ini dinle
    const handleUserUpdate = (event: CustomEvent) => {
      const updatedUser = event.detail;
      if (updatedUser) {
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    };
    
    window.addEventListener('userUpdated', handleUserUpdate as EventListener);
    
    return () => {
      window.removeEventListener('userUpdated', handleUserUpdate as EventListener);
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const authState = await authService.login({ email, password });
      setUser(authState.user);
      // Role'ü set etme - kullanıcı role-signin sayfasında seçsin
      // setSelectedRole(authState.user?.role || null);
    } catch (error) {
      throw error;
    }
  }, []);

  const register = useCallback(async (data: any) => {
    try {
      const authState = await authService.register(data);
      setUser(authState.user);
      setSelectedRole(authState.user?.role || null);
    } catch (error) {
      throw error;
    }
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await authService.logout();
    } catch (error) {
    } finally {
      setUser(null);
      setSelectedRole(null);
    }
  }, []);

  const updateSelectedRole = useCallback((role: string | null): void => {
    setSelectedRole(role);
  }, []);

  const value: AuthContextType = {
    user,
    initializing,
    selectedRole,
    setSelectedRole: updateSelectedRole,
    signOut,
    login,
    register
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

